import type { ComponentWidgetConfig, WidgetType } from 'components/shared/base-product-tile/base-product-tile-types.js'
import { renameDeprecatedWidgetProperties } from 'components/shared/base-product-tile/deprecated-widget-properties.js'
import { triggerVariant as orderTemplateTriggerVariants } from 'components/discovery/product/add-to-order-templates/components/order-template-slide-in/order-template-slide-in.js'

export interface ReplacedWidget {
  type: WidgetType
  properties: ComponentWidgetConfig
}

const RENAMED_ORDER_TEMPLATE_PROPERTIES: Record<string, string> = {
  variant: 'triggerType',
  styleVariant: 'triggerVariant',
  size: 'triggerSize',
  iconName: 'triggerIconName',
}

const toCamelCaseKeys = (properties: ComponentWidgetConfig): ComponentWidgetConfig =>
  Object.fromEntries(Object.entries(properties).map(([key, value]) => [key.replace(/-([a-z])/g, (_, char: string) => char.toUpperCase()), value]))

const isOrderTemplateTriggerVariant = (value: unknown): value is typeof orderTemplateTriggerVariants[number] =>
  (orderTemplateTriggerVariants as readonly unknown[]).includes(value)

const replaceAddToOrderTemplate = (properties: ComponentWidgetConfig): ReplacedWidget => {
  const { asLink, variant, iconName } = toCamelCaseKeys(properties)
  return {
    type: 'csc-order-template-slide-in',
    properties: {
      triggerType: asLink === true || asLink === 'true' ? 'link' : 'icon-button',
      ...(isOrderTemplateTriggerVariant(variant) && { triggerVariant: variant }),
      ...(typeof iconName === 'string' && iconName && { triggerIconName: iconName }),
    },
  }
}

const renameOrderTemplateProperties = (properties: ComponentWidgetConfig): ReplacedWidget => ({
  type: 'csc-order-template-slide-in',
  properties: Object.fromEntries(Object.entries(toCamelCaseKeys(properties)).map(([key, value]) => [RENAMED_ORDER_TEMPLATE_PROPERTIES[key] ?? key, value])),
})

// The configurator has no "None" unit of measure source any more: hiding it is the 'uom' hidden
// field. An order form without a source showed no unit, so it imports as a hidden unit of measure
// with the default source, which renders the same and gives the select a valid value.
const withHiddenUomForMissingSource = (properties: ComponentWidgetConfig): ComponentWidgetConfig => {
  if (properties['uomSource']) return properties
  const hiddenFields = new Set(String(properties['hiddenFields'] ?? '').split(',').map(field => field.trim()).filter(Boolean))
  hiddenFields.add('uom')
  return { ...properties, uomSource: 'default_uom', hiddenFields: [...hiddenFields].join(',') }
}

export const replaceDeprecatedWidget = (type: WidgetType, properties: ComponentWidgetConfig = {}): ReplacedWidget => {
  if (type === 'csc-product-tile-add-to-order-template') return replaceAddToOrderTemplate(properties)
  if (type === 'csc-order-template-slide-in') return renameOrderTemplateProperties(properties)
  const renamed = renameDeprecatedWidgetProperties(type, properties)
  return { type, properties: type === 'csc-order-form' ? withHiddenUomForMissingSource(renamed) : renamed }
}
